import DataTable from 'Components/DataTable';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { deps } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useMyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import { formatScore } from 'Routes/MyService/util';
import browserLanguage from 'Utility/browserLanguage';
import { useAppSelector } from 'Store/index';

const { calculateProgress } = deps;

const depsFields: Array<keyof Omit<IDEPS, 'date'>> = [
  'insomnia',
  'melancholy',
  'effort',
  'lowEnergy',
  'lonely',
  'hopelessFuture',
  'enjoyLife',
  'worthless',
  'joyOfLife',
  'melancholyWithFamily',
];

export const Deps = (): React.JSX.Element => {
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const { editing, setEditingData, fm, viewing, setViewingObj, setEditingObj } = useMyServiceContext<'deps'>();

  const depsData = editing?.data ?? viewing?.data ?? {};
  const depsLocales = deps.getLocalizations(locale);

  const onChangeSurveyForm = (values: TOnChangeValues): void => {
    const field = Object.keys(values)[0];
    if (!field) return;
    const value = Object.values(values)[0];
    setEditingData?.({
      ...depsData,
      [field]: value,
    });
  };

  const optionScorePairs: Record<NonNullable<IDEPS['insomnia']>, number> = {
    none: 0,
    little: 1,
    quiteMuch: 2,
    veryMuch: 3,
  };

  return (
    <>
      {!viewing ? (
        <div style={{ width: '80%', color: colors.tertiaryText, marginBottom: '2rem' }}>
          <p>{depsLocales.surveyInfo}</p>
          <p>{depsLocales.surveyFillingInfo}</p>
        </div>
      ) : null}
      <BlockWrapper
        title={viewing ? formatPartialDate(depsData.date) : undefined}
        buttons={
          viewing
            ? [
                {
                  title: 'general.edit',
                  onClick: () => {
                    setViewingObj(null);
                    setEditingObj({ type: 'deps', data: depsData });
                  },
                },
              ]
            : undefined
        }
      >
        {!viewing && (
          <FormRow title={'general.date'} headerWidth={5.63}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: depsData.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        <div style={{ width: viewing ? '65%' : '60%', marginBottom: '2rem', marginTop: viewing ? '6rem' : undefined }}>
          <DataTable
            headers={['', viewing ? <div key={`answer-header`} style={{ width: '15rem' }} /> : ''].concat(
              viewing ? [<div key={`score-header`} style={{ width: '5rem' }} />] : [],
            )}
            data={{
              rowData: depsFields.map(f => {
                const value = depsData[f];
                return [
                  depsLocales[f],
                  <InputHandler
                    key={f}
                    name={f}
                    type="Radio"
                    editing={!!editing}
                    formData={{
                      document: { [f]: value },
                      onChange: onChangeSurveyForm,
                    }}
                    options={['none', 'little', 'quiteMuch', 'veryMuch']}
                    optionFormatter={o => depsLocales[`opts.${o}`] ?? '-'}
                  />,
                ].concat(
                  viewing
                    ? [
                        <span key={`${f}-score`} style={{ fontWeight: 600 }}>
                          {value ? optionScorePairs[value] : '-'}
                        </span>,
                      ]
                    : [],
                );
              }),
              rowStyle: viewing ? undefined : depsFields.map(() => ({ rowAlign: 'top' })),
            }}
          />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: viewing ? '65%' : '60%',
            color: colors.tertiaryText,
            marginBottom: '0.5rem',
          }}
        >
          <span style={{ fontWeight: 600 }}>{fm('myService.sleepApnea.deps.score')}</span>
          <span style={{ fontWeight: 600, width: viewing ? '5.5rem' : '16rem', paddingLeft: '0.5rem' }}>
            {(() => {
              const { yielded, ceiling } = calculateProgress(depsData);
              return formatScore({ yielded, ceiling });
            })()}
          </span>
        </div>
        <div style={{ color: colors.tertiaryText, width: '60%' }}>{fm('myService.sleepApnea.deps.scoreInfo')}</div>
      </BlockWrapper>
      <div style={{ color: colors.tertiaryText, fontStyle: 'italic' }}>
        <p>{fm('myService.sleepApnea.deps.reference')}</p>
      </div>
    </>
  );
};
